import { memo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useFormSubmit from 'hooks/useFormSubmit';
import { useTranslation, Trans } from 'react-i18next';
import moment from 'moment';
import { Link } from 'react-router-dom';

import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import StatusBadge from 'components/ui/StatusBadge';
import ContractInfoTables from './ContractInfoTables';
import CLIENT_TYPE from 'shared/clientTypeList';
import REPAIR_TYPE from 'shared/repairTypeList';
import getLabelByTypeList from 'utils/getLabelByTypeList';
import ImagesSwiper from 'components/ImagesSwiper';
import { NoImageIcon } from 'customIcons';

/**
 * Asosiy pul ko'rsatkichi — katta, tepada.
 * tone: faqat semantik holat uchun (paid=success, debt=danger), qolgani neytral.
 */
const TONE_TEXT = {
  ink: 'text-ink',
  success: 'text-success-700',
  danger: 'text-danger-700',
  warning: 'text-warning-700',
};

const BigStat = ({ label, value, tone = 'ink' }) => (
  <div className='bg-white rounded-lg border border-line px-4 py-3 min-w-0'>
    <div className='text-[12px] font-medium text-muted mb-1 truncate'>
      {label}
    </div>
    <div
      className={`text-[19px] font-semibold leading-snug tabular-nums ${
        TONE_TEXT[tone] || TONE_TEXT.ink
      }`}
    >
      {value}
    </div>
  </div>
);

const SmallStat = ({ label, value, tone = 'ink', onClick, hint }) => (
  <div
    className={`bg-white rounded-lg border border-line px-4 py-2.5 min-w-0 ${
      onClick ? 'cursor-pointer hover:border-gray-300' : ''
    }`}
    onClick={onClick}
    title={hint}
  >
    <div className='text-[11px] text-muted mb-0.5 truncate flex items-center gap-1'>
      {label}
      {onClick && <i className='bi bi-info-circle text-[10px]' />}
    </div>
    <div
      className={`text-[14px] font-medium leading-snug tabular-nums ${
        TONE_TEXT[tone] || TONE_TEXT.ink
      }`}
    >
      {value}
    </div>
  </div>
);

const InfoRow = ({ label, value, icon }) => (
  <div className='flex items-start gap-3 py-2 border-b border-gray-50 last:border-0'>
    <div className='w-7 h-7 rounded-lg bg-surface flex items-center justify-center shrink-0 mt-0.5'>
      <i className={`${icon} text-muted text-xs`} />
    </div>
    <div className='flex flex-col min-w-0'>
      <span className='text-xs text-muted'>{label}</span>
      <span className='text-sm font-medium text-ink break-words'>
        {value || '—'}
      </span>
    </div>
  </div>
);

function ContractViewModern({
  contractQuery,
  paymentsListQuery,
  id,
  totalLeftByMonth,
  refetch,
  setRefetch,
}) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { submit: submitRefund, isSubmitting: isRefundSubmitting } =
    useFormSubmit();
  const [penaltyHistoryOpen, setPenaltyHistoryOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundFormOpen, setRefundFormOpen] = useState(false);
  const emptyRefundForm = {
    sum: '',
    date: moment().format('YYYY-MM-DD'),
    comment: '',
    target_contract_id: '',
  };
  const [refundForm, setRefundForm] = useState(emptyRefundForm);

  // "Boshqa shartnoma ID" kiritilganda — 400ms kutib, o'sha shartnoma
  // ma'lumotini (raqami + mijoz ismi) topib ko'rsatish uchun debounce.
  const debouncedTargetId = useDebounce(refundForm.target_contract_id, 400);

  const {
    data: targetContractPreview,
    isFetching: targetContractPreviewLoading,
  } = useQuery({
    queryKey: ['/admin/contract/edit', debouncedTargetId],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/admin/contract/edit/${debouncedTargetId}`,
      );
      return response.data.data;
    },
    enabled: Number(debouncedTargetId) > 0,
    retry: false,
  });

  // Peniya — admin "Sozlash" bilan yashirib/ko'rsatishi mumkin (default: ko'rinadi).
  // Hook shartsiz (erta return'dan oldin) chaqirilishi kerak.
  const { data: penaltySetting } = useQuery({
    queryKey: ['/admin/penalty-settings'],
    queryFn: async () => {
      const response = await axiosPrivate.get('/admin/penalty-settings');
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Peniya manbasi — modal ochilgandagina so'raladi
  const { data: penaltyHistory, isLoading: penaltyHistoryLoading } = useQuery({
    queryKey: ['/admin/payment/penalty-history', id],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/admin/payment/penalty-history/${id}`);
      return response.data.data;
    },
    enabled: penaltyHistoryOpen && !!id,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Mijozga qaytarilgan pul tarixi — modal ochilgandagina so'raladi
  const {
    data: refundHistory,
    isLoading: refundHistoryLoading,
    refetch: refetchRefundHistory,
  } = useQuery({
    queryKey: ['/admin/payment/refund-history', id],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/admin/payment/refund-history/${id}`);
      return response.data.data;
    },
    // Peniyadan farqli o'laroq bu qiymat shartnoma javobida yo'q — kartadagi
    // jami summani darhol ko'rsatish uchun modal ochilishini kutmay so'raladi.
    enabled: !!id,
    staleTime: 60 * 1000,
    retry: false,
  });

  const handleRefundSubmit = (e) => {
    e.preventDefault();
    submitRefund(
      { type: 'post', contentType: 'simple' },
      {
        sum: refundForm.sum,
        date: refundForm.date || undefined,
        comment: refundForm.comment || undefined,
        target_contract_id: refundForm.target_contract_id || undefined,
      },
      `/admin/payment/refund/${id}`,
      { title: t('contract.view.refundHistory.form.success') },
      null,
      true,
      () => {
        setRefundForm(emptyRefundForm);
        setRefundFormOpen(false);
        refetchRefundHistory();
      },
    );
  };

  const data = contractQuery?.data;
  if (!data) return null;

  const isValute = data?.isvalute;
  const suffix = isValute == '1' ? ' $' : ' UZS';
  const isPhysical = data?.custom?.client_type == CLIENT_TYPE.PHYSICAL?.code;
  const isLegal = data?.custom?.client_type == CLIENT_TYPE.LEGAL?.code;

  const clientName = isPhysical
    ? `${data?.custom?.surname || ''} ${data?.custom?.name || ''} ${
        data?.custom?.middlename || ''
      }`.trim()
    : data?.custom?.name;

  const statusVariant =
    data?.status == 3 ? 'danger' : data?.status == 2 ? 'success' : 'primary';
  const statusLabel =
    data?.status == 3
      ? t('common.typeList.contractStatus.cancel')
      : data?.status == 2
      ? t('contract.view.completed')
      : t('common.typeList.contractStatus.active');

  const homes = data?.homelist?.length
    ? data.homelist
    : data?.homes
    ? [{ home: data.homes }]
    : [];

  const hasDebt =
    isValute == '2'
      ? Number(data?.leftsum) > 0 || Number(data?.leftvalute) > 0
      : Number(data?.left) > 0;

  // Terassa ajratmasi. Eski yozuvlarda square/total_square semantikasi
  // aralash — JAMI maydon ikkalasining kattasidan olinadi, ichki = jami -
  // terassa. Narxlar 1 xonagacha yaxlitlanadi (butun bo'lsa kasrsiz).
  const terassaArea = Number(data?.terassa) || 0;
  const hasTerassa = terassaArea > 0 && Number(data?.terassa_sum) > 0;

  const showPenalty =
    (penaltySetting?.show_on_contract ?? true) && Number(data?.peniya) > 0;
  const jamiArea = Math.max(
    Number(data?.square) || 0,
    Number(data?.total_square) || 0,
  );
  const liveArea = jamiArea - terassaArea > 0 ? jamiArea - terassaArea : jamiArea;
  const round1 = (v) => Math.round(Number(v || 0) * 10) / 10;
  const livePricePerM =
    liveArea > 0 ? round1(Number(data?.live_sum) / liveArea) : 0;
  const terassaPricePerM = Number(data?.terassa_price)
    ? round1(data?.terassa_price)
    : terassaArea > 0
      ? round1(Number(data?.terassa_sum) / terassaArea)
      : 0;
  const contractRepaired = Number(data?.isrepaired) === 1;

  return (
    <div className='px-4 pb-6 flex flex-col gap-3'>
      {/* ═══ ASOSIY RAQAMLAR — tepada, to'liq kenglikda ═══ */}
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
        <BigStat
          label={t('contract.view.header.sum')}
          value={
            isValute == '2' ? (
              <span className='flex flex-col gap-0.5'>
                <CurrencyFormat
                  allowNegative
                  value={data?.totalsum}
                  suffix=' UZS'
                />
                <CurrencyFormat
                  allowNegative
                  value={data?.totalvalute}
                  suffix=' $'
                />
              </span>
            ) : (
              <span className='flex flex-col'>
                <CurrencyFormat
                  allowNegative
                  value={data?.sum - data?.discount}
                  suffix={suffix}
                />
                {/* Terassali shartnoma: turar/terassa ajratmasi */}
                {hasTerassa && (
                  <span className='text-[12px] text-muted font-normal'>
                    {t('contract.view.header.living')}:{' '}
                    <CurrencyFormat
                      value={Number(data?.live_sum) || 0}
                      suffix={suffix}
                    />
                    {' • '}
                    {t('contract.view.header.terassa')}:{' '}
                    <CurrencyFormat
                      value={Number(data?.terassa_sum) || 0}
                      suffix={suffix}
                    />
                  </span>
                )}
              </span>
            )
          }
        />
        <BigStat
          label={t('payment.add.contract.paidSum')}
          tone='success'
          value={
            isValute == '2' ? (
              <span className='flex flex-col gap-0.5'>
                <CurrencyFormat
                  allowNegative
                  value={data?.totalsum - data?.leftsum}
                  suffix=' UZS'
                />
                <CurrencyFormat
                  allowNegative
                  value={data?.totalvalute - data?.leftvalute}
                  suffix=' $'
                />
              </span>
            ) : (
              <CurrencyFormat
                allowNegative
                value={data?.sum - data?.left - data?.discount}
                suffix={suffix}
              />
            )
          }
        />
        <BigStat
          label={t('contract.view.header.leftPrice')}
          tone={hasDebt ? 'danger' : 'ink'}
          value={
            isValute == '2' ? (
              <span className='flex flex-col gap-0.5'>
                <CurrencyFormat
                  allowNegative
                  value={data?.leftsum}
                  suffix=' UZS'
                />
                <CurrencyFormat
                  allowNegative
                  value={data?.leftvalute}
                  suffix=' $'
                />
              </span>
            ) : (
              <CurrencyFormat allowNegative value={data?.left} suffix={suffix} />
            )
          }
        />
      </div>

      {/* Ikkilamchi ko'rsatkichlar — ixcham */}
      <div
        className={`grid grid-cols-1 gap-3 ${
          (Number(data?.terassa) > 0 ? 4 : 3) + (showPenalty ? 1 : 0) + 1 >= 5
            ? 'sm:grid-cols-3 lg:grid-cols-5'
            : Number(data?.terassa) > 0
              ? 'sm:grid-cols-4'
              : 'sm:grid-cols-3'
        }`}
      >
        <SmallStat
          label={t('contract.view.header.startPrice')}
          value={
            <CurrencyFormat
              allowNegative
              value={data?.start_price}
              suffix={suffix}
            />
          }
        />
        <SmallStat
          label={t('contract.view.header.monthlyLeft')}
          tone={totalLeftByMonth ? 'warning' : 'ink'}
          value={
            totalLeftByMonth ? (
              <CurrencyFormat
                value={totalLeftByMonth}
                allowNegative
                suffix={isValute == '0' ? ' UZS' : ' $'}
              />
            ) : (
              <span className='text-muted font-normal text-xs'>
                {t('contract.view.header.noMonthlyLeft')}
              </span>
            )
          }
        />
        <SmallStat
          label='kv/narx'
          value={
            hasTerassa ? (
              // 1 m² narxi — turar joy va terassa uchun alohida
              <span className='flex flex-col gap-0.5 text-[13px]'>
                <span>
                  {t('contract.view.header.living')}:{' '}
                  <CurrencyFormat value={livePricePerM} suffix={suffix} />
                </span>
                <span>
                  {t('contract.view.header.terassa')}:{' '}
                  <CurrencyFormat value={terassaPricePerM} suffix={suffix} />
                </span>
              </span>
            ) : (
              <CurrencyFormat
                allowNegative
                value={
                  data?.square
                    ? ((data?.sum - data?.discount) / data?.square).toFixed(1)
                    : 0
                }
                suffix={suffix}
              />
            )
          }
        />
        {/* Terassa — maydoni va summasi (bor bo'lsa) */}
        {Number(data?.terassa) > 0 && (
          <SmallStat
            label={t('contract.view.header.terassa')}
            value={
              <span className='flex flex-col gap-0.5'>
                <span>
                  {Number(data?.terassa)} m²
                </span>
                <CurrencyFormat
                  allowNegative
                  value={data?.terassa_sum}
                  suffix={suffix}
                />
              </span>
            }
          />
        )}
        {/* Peniya — faqat jami summa 0 dan katta va sozlamada yoqilgan bo'lsa */}
        {showPenalty && (
          <SmallStat
            label={t('contract.view.header.penalty')}
            tone='danger'
            hint={t('contract.view.penaltyHistory.hint')}
            onClick={() => setPenaltyHistoryOpen(true)}
            value={
              <CurrencyFormat allowNegative value={data?.peniya} suffix={suffix} />
            }
          />
        )}
        {/* Mijozga qaytarilgan pul — doim ko'rinadi, chunki qo'lda kiritiladi */}
        <SmallStat
          label={t('contract.view.header.refund')}
          tone={refundHistory?.total > 0 ? 'warning' : 'ink'}
          hint={t('contract.view.refundHistory.hint')}
          onClick={() => setRefundOpen(true)}
          value={
            refundHistory?.total > 0 ? (
              <CurrencyFormat
                allowNegative
                value={refundHistory?.total}
                suffix={suffix}
              />
            ) : (
              <span className='text-muted font-normal text-xs'>
                {t('contract.view.refundHistory.add')}
              </span>
            )
          }
        />
      </div>

      {/* Peniya manbasi — qaysi kuni, qancha qarzdan, qanday stavkada qo'shilgani */}
      <Dialog
        open={penaltyHistoryOpen}
        onClose={() => setPenaltyHistoryOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle className='flex items-center justify-between'>
          <span>{t('contract.view.penaltyHistory.title')}</span>
          <IconButton onClick={() => setPenaltyHistoryOpen(false)} size='small'>
            <i className='bi bi-x-lg text-base' />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {penaltyHistoryLoading ? (
            <div className='py-8 text-center text-muted text-sm'>
              {t('common.global.loading')}
            </div>
          ) : penaltyHistory?.rows?.length ? (
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between items-center bg-danger-50 rounded-lg px-3 py-2 mb-1'>
                <span className='text-sm font-medium text-danger-700'>
                  {t('contract.view.penaltyHistory.total')}
                </span>
                <b className='text-danger-700'>
                  <CurrencyFormat
                    allowNegative
                    value={penaltyHistory?.total}
                    suffix={suffix}
                  />
                </b>
              </div>
              {penaltyHistory.rows.map((row) => (
                <div
                  key={row.id}
                  className='flex items-center justify-between border border-line rounded-lg px-3 py-2'
                >
                  <div className='flex flex-col'>
                    <span className='text-sm text-ink'>
                      {moment(row.date).format('DD.MM.YYYY')}
                    </span>
                    {row.is_manual ? (
                      <span className='text-[11px] text-muted'>
                        {t('contract.view.penaltyHistory.manual')}
                      </span>
                    ) : (
                      <span className='text-[11px] text-muted'>
                        {t('contract.view.penaltyHistory.fromDebt')}:{' '}
                        <CurrencyFormat
                          allowNegative
                          value={row.debt_sum}
                          suffix={suffix}
                        />{' '}
                        × {row.rate}%
                        {row.payment_list?.payment_date && (
                          <>
                            {' '}
                            ({t('contract.view.penaltyHistory.dueDate')}:{' '}
                            {moment(row.payment_list.payment_date).format(
                              'DD.MM.YYYY',
                            )}
                            )
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  <b className='text-sm text-danger-700 shrink-0 ml-3'>
                    +
                    <CurrencyFormat allowNegative value={row.sum} suffix={suffix} />
                  </b>
                </div>
              ))}
            </div>
          ) : (
            <div className='py-8 text-center text-muted text-sm'>
              {t('common.global.noDataFound')}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mijozga qaytarilgan pul — tarix + yangi yozuv qo'shish */}
      <Dialog
        open={refundOpen}
        onClose={() => {
          setRefundOpen(false);
          setRefundFormOpen(false);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle className='flex items-center justify-between'>
          <span>{t('contract.view.refundHistory.title')}</span>
          <IconButton
            onClick={() => {
              setRefundOpen(false);
              setRefundFormOpen(false);
            }}
            size='small'
          >
            <i className='bi bi-x-lg text-base' />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className='flex flex-col gap-2'>
            {refundHistory?.total > 0 && (
              <div className='flex justify-between items-center bg-warning-50 rounded-lg px-3 py-2'>
                <span className='text-sm font-medium text-warning-700'>
                  {t('contract.view.refundHistory.total')}
                </span>
                <b className='text-warning-700'>
                  <CurrencyFormat
                    allowNegative
                    value={refundHistory?.total}
                    suffix={suffix}
                  />
                </b>
              </div>
            )}

            {refundFormOpen ? (
              <form
                onSubmit={handleRefundSubmit}
                className='flex flex-col gap-2 border border-line rounded-lg p-3'
              >
                <TextField
                  size='small'
                  type='number'
                  label={t('contract.view.refundHistory.form.sum')}
                  value={refundForm.sum}
                  required
                  inputProps={{ step: '0.01', min: '0.01' }}
                  onChange={(e) =>
                    setRefundForm((f) => ({ ...f, sum: e.target.value }))
                  }
                />
                <TextField
                  size='small'
                  type='date'
                  label={t('contract.view.refundHistory.form.date')}
                  value={refundForm.date}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    setRefundForm((f) => ({ ...f, date: e.target.value }))
                  }
                />
                <TextField
                  size='small'
                  label={t('contract.view.refundHistory.form.comment')}
                  value={refundForm.comment}
                  multiline
                  minRows={2}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    setRefundForm((f) => ({ ...f, comment: e.target.value }))
                  }
                />
                <TextField
                  size='small'
                  type='number'
                  label={t('contract.view.refundHistory.form.targetContractId')}
                  helperText={t(
                    'contract.view.refundHistory.form.targetContractHint',
                  )}
                  value={refundForm.target_contract_id}
                  inputProps={{ step: '1', min: '1' }}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    setRefundForm((f) => ({
                      ...f,
                      target_contract_id: e.target.value,
                    }))
                  }
                />
                {/* Boshqa shartnoma ID kiritilganda — topilgan shartnoma
                    ma'lumotini ko'rsatib, xato ID kiritishdan saqlaydi */}
                {Number(refundForm.target_contract_id) > 0 && (
                  <div className='rounded-lg border border-line px-3 py-2 text-xs'>
                    {targetContractPreviewLoading ||
                    String(refundForm.target_contract_id) !==
                      String(debouncedTargetId) ? (
                      <span className='text-muted'>
                        {t('common.global.loading')}
                      </span>
                    ) : targetContractPreview?.id ? (
                      <span className='text-ink'>
                        <i className='bi bi-check-circle-fill text-success-700 mr-1' />
                        {t('contract.view.refundHistory.form.foundContract')}
                        {': № '}
                        <b>{targetContractPreview.name}</b>
                        {' — '}
                        {targetContractPreview.custom?.surname}{' '}
                        {targetContractPreview.custom?.name}
                      </span>
                    ) : (
                      <span className='text-danger-700'>
                        <i className='bi bi-exclamation-circle-fill mr-1' />
                        {t('contract.view.refundHistory.form.contractNotFound')}
                      </span>
                    )}
                  </div>
                )}
                <div className='flex justify-end gap-2 pt-1'>
                  <Button
                    variant='text'
                    onClick={() => {
                      setRefundFormOpen(false);
                      setRefundForm(emptyRefundForm);
                    }}
                  >
                    {t('common.button.cancel')}
                  </Button>
                  <Button
                    variant='base'
                    color='success'
                    type='submit'
                    disabled={isRefundSubmitting || !refundForm.sum}
                  >
                    {isRefundSubmitting ? (
                      <CircularProgress size={18} />
                    ) : (
                      t('contract.view.refundHistory.form.submit')
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <Button
                variant='outlined'
                size='small'
                onClick={() => setRefundFormOpen(true)}
                className='self-start'
              >
                <i className='bi bi-plus-lg mr-1' />
                {t('contract.view.refundHistory.add')}
              </Button>
            )}

            {refundHistoryLoading ? (
              <div className='py-8 text-center text-muted text-sm'>
                {t('common.global.loading')}
              </div>
            ) : refundHistory?.rows?.length ? (
              refundHistory.rows.map((row) => (
                <div
                  key={row.id}
                  className='flex items-center justify-between border border-line rounded-lg px-3 py-2'
                >
                  <div className='flex flex-col'>
                    <span className='text-sm text-ink'>
                      {moment(row.date).format('DD.MM.YYYY')}
                    </span>
                    {row.target_contract && (
                      <span className='text-[11px] text-muted'>
                        {t('contract.view.refundHistory.targetContract')}:{' '}
                        {row.target_contract.name}
                      </span>
                    )}
                    {row.comment && (
                      <span className='text-[11px] text-muted'>
                        {row.comment}
                      </span>
                    )}
                  </div>
                  <b className='text-sm text-warning-700 shrink-0 ml-3'>
                    <CurrencyFormat
                      allowNegative
                      value={row.sum}
                      suffix={suffix}
                    />
                  </b>
                </div>
              ))
            ) : (
              <div className='py-8 text-center text-muted text-sm'>
                {t('common.global.noDataFound')}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ PASTKI QISM: chapda mijoz, o'ngda uy + jadvallar ═══ */}
      <div className='grid grid-cols-12 gap-4 items-start'>
        {/* Mijoz kartasi */}
        <div className='col-span-12 lg:col-span-4 bg-white rounded-lg border border-line p-5 flex flex-col gap-3'>
          <div className='flex items-center gap-3'>
            <div className='w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary font-bold text-lg shrink-0'>
              {clientName?.[0]?.toUpperCase() || '?'}
            </div>
            <div className='flex flex-col min-w-0'>
              <Link
                to={`/admin/client/view/${data?.custom?.id}`}
                className='no-underline font-semibold text-ink text-base truncate hover:text-primary'
              >
                {clientName}
              </Link>
              <div className='flex items-center gap-2 mt-1 flex-wrap'>
                <StatusBadge variant={statusVariant}>{statusLabel}</StatusBadge>
                {data?.type_name?.name && (
                  <StatusBadge variant='neutral'>
                    {data.type_name.name}
                  </StatusBadge>
                )}
              </div>
            </div>
          </div>

          <div className='h-px bg-gray-100' />

          {isPhysical && (
            <>
              <InfoRow
                label={t('contract.view.clientPhysical.phone')}
                icon='bi bi-telephone-fill'
                value={[data?.custom?.phone, data?.custom?.phone2]
                  .filter(Boolean)
                  .join(', ')}
              />
              <InfoRow
                label={t('contract.view.clientPhysical.passportSeries')}
                icon='bi bi-card-text'
                value={data?.custom?.detail?.passport_series}
              />
              <InfoRow
                label={t('common.fields.pnfl')}
                icon='bi bi-fingerprint'
                value={data?.custom?.detail?.pnfl}
              />
              <InfoRow
                label={t('contract.view.clientPhysical.birthday')}
                icon='bi bi-cake2'
                value={
                  data?.custom?.detail?.birthday
                    ? moment(data.custom.detail.birthday).format('DD.MM.YYYY')
                    : null
                }
              />
              <InfoRow
                label={t('contract.view.clientPhysical.issue')}
                icon='bi bi-calendar-check'
                value={
                  data?.custom?.detail?.issue
                    ? moment(data.custom.detail.issue).format('DD.MM.YYYY')
                    : null
                }
              />
              <InfoRow
                label={t('contract.view.clientPhysical.authority')}
                icon='bi bi-building'
                value={data?.custom?.detail?.authority}
              />
              <InfoRow
                label={t('contract.view.clientPhysical.home')}
                icon='bi bi-geo-alt-fill'
                value={data?.custom?.detail?.home}
              />
            </>
          )}
          {isLegal && (
            <>
              <InfoRow
                label={t('contract.view.clientLegal.phone')}
                icon='bi bi-telephone-fill'
                value={[data?.custom?.phone, data?.custom?.phone2]
                  .filter(Boolean)
                  .join(', ')}
              />
              <InfoRow
                label={t('contract.view.clientLegal.address')}
                icon='bi bi-geo-alt-fill'
                value={data?.custom?.detail?.home}
              />
              <InfoRow
                label={t('contract.view.clientLegal.accountNumber')}
                icon='bi bi-bank'
                value={data?.custom?.detail?.account_number}
              />
              <InfoRow
                label={t('contract.view.clientLegal.tin')}
                icon='bi bi-hash'
                value={data?.custom?.detail?.inn}
              />
              <InfoRow
                label={t('contract.view.clientLegal.mfo')}
                icon='bi bi-hash'
                value={data?.custom?.detail?.mfo}
              />
              <InfoRow
                label={t('contract.view.clientLegal.oked')}
                icon='bi bi-hash'
                value={data?.custom?.detail?.oked}
              />
            </>
          )}

          <div className='h-px bg-gray-100' />

          {data?.staff?.name && (
            <div className='flex items-center gap-2 text-sm'>
              <span className='w-6 h-6 rounded-full bg-gray-100 text-muted flex items-center justify-center text-xs'>
                <i className='bi bi-person-badge' />
              </span>
              <span className='text-muted shrink-0'>
                {t('contract.view.staff')}:
              </span>
              <span className='font-medium text-ink'>{data.staff.name}</span>
            </div>
          )}
          {data?.operator?.name && (
            <div className='flex items-center gap-2 text-sm'>
              <span className='w-6 h-6 rounded-full bg-gray-100 text-muted flex items-center justify-center text-xs'>
                <i className='bi bi-headset' />
              </span>
              <span className='text-muted shrink-0'>
                {t('contract.view.operator')}:
              </span>
              <span className='font-medium text-ink'>
                {data.operator.name}
              </span>
            </div>
          )}
          {data?.comment && (
            <div className='rounded-lg bg-surface px-4 py-3 text-sm text-gray-600 border border-gray-100'>
              <span className='font-medium text-primary'>
                {t('contract.view.header.comment')}:{' '}
              </span>
              {data.comment}
            </div>
          )}
        </div>

        {/* Uy(lar) + jadvallar */}
        <div className='col-span-12 lg:col-span-8 flex flex-col gap-4 min-w-0'>
          {homes.map(({ home, square, terassa }, idx) => {
            square = Number(square);
            // Shartnomada muhrlangan terassa (pivot); eski shartnomalarda
            // pivot bo'sh — uyning o'zidagi qiymat ko'rsatiladi
            const terassaArea = Number(terassa ?? home?.terassa ?? 0);
            const imgs = [
              home?.plan?.link,
              home?.plan?.link2d,
              home?.plan?.location,
            ].filter(Boolean);

            return (
              <div
                key={home?.id || idx}
                className='bg-white rounded-lg border border-line p-5 flex gap-4'
              >
                <div className='w-40 h-40 shrink-0 rounded-xl overflow-hidden bg-surface border border-gray-100 flex items-center justify-center'>
                  {imgs.length ? (
                    <ImagesSwiper
                      removeNavigation
                      imageURLs={imgs}
                      className='!h-40 w-40 object-contain'
                      alt=''
                    />
                  ) : (
                    <NoImageIcon />
                  )}
                </div>

                <div className='flex flex-col gap-1.5 flex-1 min-w-0'>
                  <div className='font-semibold text-ink text-sm mb-1 flex items-center gap-2'>
                    <span>
                      {home?.blocks?.objects?.name} — {home?.blocks?.name}
                    </span>
                    {/* Shartnoma darajasidagi ta'mir holati */}
                    <span
                      className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${
                        contractRepaired
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {contractRepaired
                        ? t('common.typeList.repairType.repaired')
                        : t('common.typeList.repairType.notRepaired')}
                    </span>
                  </div>
                  {[
                    {
                      label: t('contract.view.homeDetail.homeNumber'),
                      value: home?.number,
                      icon: 'bi bi-door-open',
                    },
                    {
                      label: t('contract.view.homeDetail.stage'),
                      value: home?.stage,
                      icon: 'bi bi-layers',
                    },
                    {
                      label: t('contract.view.homeDetail.rooms'),
                      value: home?.rooms,
                      icon: 'bi bi-grid-1x2',
                    },
                    {
                      label: t('contract.view.homeDetail.area'),
                      icon: 'bi bi-aspect-ratio',
                      value:
                        home?.square != square ? (
                          <>
                            <span className='line-through text-gray-400 mr-1'>
                              {home?.square}{' '}
                              <Trans i18nKey='common.global.meter'>
                                m<sup>2</sup>
                              </Trans>
                            </span>
                            {square}{' '}
                            <Trans i18nKey='common.global.meter'>
                              m<sup>2</sup>
                            </Trans>
                          </>
                        ) : (
                          <>
                            {home?.square}{' '}
                            <Trans i18nKey='common.global.meter'>
                              m<sup>2</sup>
                            </Trans>
                          </>
                        ),
                    },
                    // Terassa maydoni — bor bo'lsa ko'rsatiladi
                    ...(terassaArea > 0
                      ? [
                          {
                            label: t('contract.view.homeDetail.terassa'),
                            icon: 'bi bi-border-outer',
                            value: (
                              <>
                                {terassaArea}{' '}
                                <Trans i18nKey='common.global.meter'>
                                  m<sup>2</sup>
                                </Trans>
                              </>
                            ),
                          },
                        ]
                      : []),
                    {
                      label: t('contract.view.homeDetail.isrepaired'),
                      value:
                        getLabelByTypeList(REPAIR_TYPE, home?.isrepaired) ||
                        '—',
                      icon: 'bi bi-tools',
                    },
                  ].map((row, i) => (
                    <div key={i} className='flex items-center gap-2 text-xs'>
                      <i className={`${row.icon} text-gray-300 w-4`} />
                      <span className='text-muted shrink-0'>{row.label}:</span>
                      <span className='font-medium text-ink'>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {id && (
            <ContractInfoTables
              id={id}
              contractQuery={contractQuery}
              paymentsListQuery={paymentsListQuery}
              refetch={refetch}
              setRefetch={setRefetch}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(ContractViewModern);
